import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtQty } from "@/lib/api";
import { PageHeader, Field, Combobox } from "@/components/common";

const PER_PAGE = 24;

export default function QrStickers() {
  const [drums, setDrums] = useState([]);
  const [size, setSize] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/drums", { params: { kind: "drum,offcut,cut_piece", min_balance: 0.001 } }).then((r) => setDrums(r.data.filter((d) => ["full", "partial", "awaiting_dispatch"].includes(d.status)))); }, []);
  const sizes = useMemo(() => [...new Set(drums.map((d) => d.cable_size))].sort((a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b)).map((s) => ({ value: s, label: s })), [drums]);
  const list = drums.filter((d) => (!size || d.cable_size === size) && (!q || `${d.drum_code} ${d.mfr_drum_no} ${d.brand}`.toLowerCase().includes(q.toLowerCase())));
  const pages = [];
  for (let i = 0; i < list.length; i += PER_PAGE) pages.push(list.slice(i, i + PER_PAGE));
  return (
    <div className="space-y-4">
      <style>{"@media print { @page { size: A4 portrait; margin: 6mm; } .qr-page { break-after: page; } .print-area { border: 0 !important; } }"}</style>
      <PageHeader title="QR Drum Stickers" subtitle="Print on an A4 sticker sheet (24 per page, 3 × 8) and stick one on every drum. Staff scan it in Cutting and Dispatch.">
        <Button onClick={() => window.print()} disabled={!list.length} data-testid="qr-print-btn"><Printer className="h-4 w-4 mr-1" />Print {list.length} sticker(s)</Button>
      </PageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <Field label="Cable size"><Combobox options={[{ value: "", label: "All sizes" }, ...sizes]} value={size} onChange={setSize} testid="qr-size-select" placeholder="All sizes" /></Field>
        <Field label="Search"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Drum code, mfr no or brand" data-testid="qr-search-input" /></Field>
      </div>
      <div className="overflow-x-auto">
        <div className="print-area bg-white text-black w-[198mm]" data-testid="qr-sheet">
          {pages.map((pg, pi) => (
            <div key={pi} className="qr-page grid grid-cols-3 gap-0 mb-4 print:mb-0">
              {pg.map((d) => (
                <div key={d.id} className="h-[35mm] border border-dashed border-gray-300 p-[2mm] flex items-center gap-[2mm] overflow-hidden" data-testid={`qr-sticker-${d.drum_code}`}>
                  <QRCodeSVG value={d.drum_code} size={96} level="M" className="shrink-0 w-[26mm] h-[26mm]" />
                  <div className="min-w-0 leading-tight">
                    <div className="text-[9px] font-bold">ROXY CABLES</div>
                    <div className="font-mono text-[11px] font-bold truncate">{d.mfr_drum_no || d.drum_code}</div>
                    <div className="font-mono text-[8px] truncate">{d.drum_code}</div>
                    <div className="text-[9px] font-semibold truncate">{d.brand}</div>
                    <div className="text-[9px] truncate">{d.cable_size}</div>
                    <div className="text-[8px] text-gray-600 truncate">{fmtQty(d.original_length, d.uom)} orig.</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
